import { useMemo } from "react";
import type { Ear } from "../../../type/tonalTest";
import { DB_MIN, DB_MAX, DB_LEVELS, FREQUENCIES, type AudiometerCursor } from "./audiometerChartConstants";

const DB_TICKS = Array.from({ length: 14 }, (_, i) => DB_MIN + i * 10);

const WIDTH = 560;
const HEIGHT = 340;
const MARGIN = { top: 16, right: 20, bottom: 36, left: 46 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;

const xForFreqIndex = (index: number) =>
  MARGIN.left + (PLOT_WIDTH * index) / (FREQUENCIES.length - 1);

const yForDb = (db: number) =>
  MARGIN.top + ((db - DB_MIN) / (DB_MAX - DB_MIN)) * PLOT_HEIGHT;

type AudiometerChartProps = {
  ear: Ear;
  color: string;
  label: string;
  acValues: (number | null)[];
  bcValues: (number | null)[];
  cursor: AudiometerCursor;
  focused: boolean;
  cursorVisible: boolean;
  containerRef: React.Ref<HTMLDivElement>;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyUp: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onPointClick: (freqIndex: number, dbIndex: number) => void;
};

export default function AudiometerChart({
  color,
  label,
  acValues,
  bcValues,
  cursor,
  focused,
  cursorVisible,
  containerRef,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  onPointClick
}: AudiometerChartProps) {
  const acPoints = useMemo(
    () =>
      acValues
        .map((db, index) => (db === null ? null : { index, db }))
        .filter((p): p is { index: number; db: number } => p !== null),
    [acValues]
  );

  const bcPoints = useMemo(
    () =>
      bcValues
        .map((db, index) => (db === null ? null : { index, db }))
        .filter((p): p is { index: number; db: number } => p !== null),
    [bcValues]
  );

  const buildLinePath = (pts: { index: number; db: number }[]) =>
    pts.length === 0
      ? ""
      : pts.map((p, i) => `${i === 0 ? "M" : "L"} ${xForFreqIndex(p.index)} ${yForDb(p.db)}`).join(" ");

  const acLinePath = useMemo(() => buildLinePath(acPoints), [acPoints]);
  const bcLinePath = useMemo(() => buildLinePath(bcPoints), [bcPoints]);

  const cursorDb = DB_LEVELS[cursor.dbIndex];
  const cursorX = xForFreqIndex(cursor.freqIndex);
  const cursorY = yForDb(cursorDb);

  const handleSvgClick = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const scaleX = WIDTH / rect.width;
    const scaleY = HEIGHT / rect.height;
    const localX = (e.clientX - rect.left) * scaleX;
    const localY = (e.clientY - rect.top) * scaleY;

    const rawFreqIndex = ((localX - MARGIN.left) / PLOT_WIDTH) * (FREQUENCIES.length - 1);
    const freqIndex = Math.min(FREQUENCIES.length - 1, Math.max(0, Math.round(rawFreqIndex)));

    const rawDbIndex = ((localY - MARGIN.top) / PLOT_HEIGHT) * (DB_LEVELS.length - 1);
    const dbIndex = Math.min(DB_LEVELS.length - 1, Math.max(0, Math.round(rawDbIndex)));

    onPointClick(freqIndex, dbIndex);
  };

  return (
    <div
      ref={containerRef}
      className={`audiometer-chart${focused ? " audiometer-chart--focused" : ""}`}
      tabIndex={0}
      role="application"
      aria-label={label}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
    >
      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="result-chart__dot" style={{ backgroundColor: color }}></span>
        <span className="audiometer-chart__label">{label}</span>
      </div>

      <div className="result-chart__frame">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="audiometer-chart__svg"
          preserveAspectRatio="xMidYMid meet"
          onClick={handleSvgClick}
        >
          {DB_TICKS.map(db => (
            <g key={`h-${db}`}>
              <line
                x1={MARGIN.left}
                x2={WIDTH - MARGIN.right}
                y1={yForDb(db)}
                y2={yForDb(db)}
                stroke="rgba(164, 175, 196, 0.5)"
                strokeDasharray="3 3"
              />
              <text x={MARGIN.left - 8} y={yForDb(db) + 4} textAnchor="end" fontSize={11} fill="#262727">
                {db}
              </text>
            </g>
          ))}

          <text
            x={12}
            y={MARGIN.top + PLOT_HEIGHT / 2}
            textAnchor="middle"
            fontSize={11}
            fill="#262727"
            transform={`rotate(-90, 12, ${MARGIN.top + PLOT_HEIGHT / 2})`}
          >
            dB HL
          </text>

          {FREQUENCIES.map((freq, index) => (
            <g key={`v-${freq}`}>
              <line
                x1={xForFreqIndex(index)}
                x2={xForFreqIndex(index)}
                y1={MARGIN.top}
                y2={HEIGHT - MARGIN.bottom}
                stroke="rgba(164, 175, 196, 0.5)"
                strokeDasharray="3 3"
              />
              <text
                x={xForFreqIndex(index)}
                y={HEIGHT - MARGIN.bottom + 18}
                textAnchor="middle"
                fontSize={11}
                fill="#262727"
              >
                {freq} Hz
              </text>
            </g>
          ))}

          <line
            x1={MARGIN.left}
            x2={WIDTH - MARGIN.right}
            y1={yForDb(20)}
            y2={yForDb(20)}
            stroke="#f28b82"
            strokeDasharray="6 4"
            strokeOpacity={0.75}
          />

          {acLinePath && <path d={acLinePath} fill="none" stroke={color} strokeWidth={2.5} />}
          {bcLinePath && (
            <path d={bcLinePath} fill="none" stroke={color} strokeWidth={2.5} strokeDasharray="8 5" />
          )}

          {acPoints.map(p => (
            <circle key={`ac-${p.index}`} cx={xForFreqIndex(p.index)} cy={yForDb(p.db)} r={5} fill={color} />
          ))}
          {bcPoints.map(p => (
            <circle key={`bc-${p.index}`} cx={xForFreqIndex(p.index)} cy={yForDb(p.db)} r={5} fill={color} />
          ))}

          {focused && (
            <circle
              className="audiometer-chart__cursor"
              cx={cursorX}
              cy={cursorY}
              r={7}
              fill="none"
              stroke={color}
              strokeWidth={2}
              style={{ opacity: cursorVisible ? 1 : 0.15 }}
            />
          )}
        </svg>
      </div>
    </div>
  );
}
